import { useCallback, useEffect, useRef } from "react";
import PageHeader from "@/components/dashboard/PageHeader";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";

// "Our Other Products" — the shared product feed from themasterdashboard.com,
// embedded for every signed-in role.
//
// The feed decides what a viewer may see from their EMAIL DOMAIN (privileged
// companies additionally see in-house products; everyone else sees the public
// ones), so the identifier is read from the session on every render and never
// hardcoded. company_id rides along as a second signal where the session has one.
//
// One component, mounted once per role shell (see App.jsx) so the page always
// renders inside the sidebar + topbar wrapper — and inside that shell's auth
// gate — rather than escaping to a bare top-level route.

const EMBED_ORIGIN = "https://themasterdashboard.com";
const EMBED_KEY = "ph_live_8a2ad56d365ef480610761682b243ce2e5fd";
// tailwind.config.js → colors.brand.500. Six hex digits, no "#": the feed ships
// with no colour of its own and stays neutral graphite unless we send one.
const ACCENT = "6366f1";
const FRAME_ID = "our-products";

// The shell's chrome above and below the frame: topbar (h-16 = 64px), <main>'s
// vertical padding (py-6, and py-8 = 64px at 2xl) and this page's own PageHeader
// (~80px including its mb-6). Subtracting them rather than using 100vh is what
// keeps the shell from growing a second scrollbar beside the feed's own.
//
// Sized for the LARGEST of those paddings, so the widest screens don't overflow;
// narrower ones just get a little slack under the frame, which costs nothing.
const FRAME_HEIGHT = "calc(100vh - 208px)";

export default function OurProducts() {
  const { user } = useAuth();
  const { theme } = useTheme();
  const frameRef = useRef(null);

  const email = user?.email || "";
  const companyId = user?.company_id || "";

  // The feed is cross-origin and cannot read our stylesheet, so the theme has to
  // be told to it twice over. In the URL, which is what makes the FIRST paint
  // correct with no messaging involved...
  const params = new URLSearchParams({ key: EMBED_KEY, variant: "page" });
  // ...only when the session actually has them. Sending an empty email would be
  // a worse signal than sending none: the viewer then sees only public products,
  // which is the correct fallback.
  if (email) params.set("email", email);
  if (companyId) params.set("company", companyId);
  // This page prints its own <h1>, so the feed must not print a second title.
  params.set("header", "none");
  // ThemeContext resolves the OS preference at startup and only ever stores
  // "light" or "dark", so this needs no further mapping — and deliberately does
  // NOT re-read prefers-color-scheme, which is the thing being corrected.
  params.set("theme", theme === "dark" ? "dark" : "light");
  params.set("accent", ACCENT);
  const src = `${EMBED_ORIGIN}/embed/products?${params.toString()}`;

  // ...and over postMessage, so flipping the app's toggle repaints the feed.
  const sendTheme = useCallback(() => {
    const frame = frameRef.current;
    if (!frame || !frame.contentWindow) return;
    frame.contentWindow.postMessage(
      {
        type: "md-products-theme",
        theme: theme === "dark" ? "dark" : "light",
        accent: ACCENT,
      },
      EMBED_ORIGIN
    );
  }, [theme]);

  // The feed announces itself on load and keeps re-announcing until answered, so
  // the listener lives on window (not the frame) and is re-attached whenever the
  // answer would change. Re-sending on every theme change covers the frame that
  // is already loaded and never asks again.
  useEffect(() => {
    function onMessage(e) {
      // Identity of the sending window, not a string compare on e.origin: this
      // both rejects a "ready" spoofed by any other frame or opener, and keeps
      // working if the feed ever redirects itself somewhere else.
      if (e.source !== frameRef.current?.contentWindow) return;
      if (e.data?.type === "md-products-ready") sendTheme();
    }
    window.addEventListener("message", onMessage);
    sendTheme();
    return () => window.removeEventListener("message", onMessage);
  }, [sendTheme]);

  return (
    <>
      <PageHeader
        title="Our Other Products"
        subtitle="More tools from our team — see what else could help you."
      />
      <iframe
        // Remounting on theme change is what guarantees a correct first paint
        // even if the ready/answer handshake is missed entirely.
        key={theme}
        id={FRAME_ID}
        ref={frameRef}
        src={src}
        title="Our products"
        loading="lazy"
        onLoad={sendTheme}
        style={{ width: "100%", height: FRAME_HEIGHT, minHeight: 520, border: 0 }}
        className="block rounded-2xl bg-white dark:bg-ink-900"
      />
    </>
  );
}
